import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { Search, Stethoscope, Briefcase, Users, ArrowRight, X } from 'lucide-react';
import { getCases } from '../features/cases/api/cases-api';
import { getOpportunities } from '../features/opportunities/api/opportunities-api';
import { searchPanels } from '../features/panels/api/panels-api';
import { useAuth } from '../contexts/AuthContext';

const TABS = ['All', 'Cases', 'Opportunities', 'Panels'];

const SUGGESTIONS = ['emergency', 'surgery', 'feline', 'canine', 'dermatology', 'internal medicine', 'diagnostics', 'cardiology'];

// ── Skeleton Card ──────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 bg-gray-100 rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-100 rounded w-3/4" />
        <div className="h-3 bg-gray-50 rounded w-1/2" />
      </div>
    </div>
  </div>
);

// ── Highlight matched query in text ───────────────────────────────────────────
const Highlight = ({ text = '', query = '' }) => {
  if (!query.trim() || !text) return <span>{text}</span>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-indigo-100 text-indigo-700 rounded px-0.5 not-italic font-semibold">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

// ── Case Result Card ───────────────────────────────────────────────────────────
const CaseCard = ({ c, query, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-indigo-300 hover:shadow-sm transition-all duration-200 group"
  >
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
        <Stethoscope size={16} className="text-indigo-500" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-indigo-700 transition-colors">
          <Highlight text={c.title} query={query} />
        </h3>
        {c.content && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
            {c.content.slice(0, 120)}{c.content.length > 120 ? '…' : ''}
          </p>
        )}
        {c.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {c.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-500 rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
      <ArrowRight size={14} className="text-gray-300 group-hover:text-indigo-400 transition-colors flex-shrink-0 mt-1" />
    </div>
  </button>
);

// ── Opportunity Result Card ────────────────────────────────────────────────────
const OpportunityCard = ({ o, query, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-emerald-300 hover:shadow-sm transition-all duration-200 group"
  >
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
        <Briefcase size={16} className="text-emerald-500" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-emerald-700 transition-colors">
          <Highlight text={o.title} query={query} />
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          <Highlight text={o.clinicName} query={query} /> · <Highlight text={o.location} query={query} />
        </p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-medium">{o.type}</span>
          {o.experienceRequired && (
            <span className="text-[10px] text-gray-400">{o.experienceRequired}</span>
          )}
        </div>
      </div>
      <ArrowRight size={14} className="text-gray-300 group-hover:text-emerald-400 transition-colors flex-shrink-0 mt-1" />
    </div>
  </button>
);

// ── Panel Result Card ──────────────────────────────────────────────────────────
const PanelCard = ({ p, query, onClick }) => (
  <button
    onClick={onClick}
    className="w-full text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-violet-300 hover:shadow-sm transition-all duration-200 group"
  >
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
        <Users size={16} className="text-violet-500" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-violet-700 transition-colors">
          <Highlight text={p.name} query={query} />
        </h3>
        {p.description && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{p.description}</p>
        )}
        <p className="text-xs text-gray-400 mt-1">{p.memberCount} members</p>
      </div>
      <ArrowRight size={14} className="text-gray-300 group-hover:text-violet-400 transition-colors flex-shrink-0 mt-1" />
    </div>
  </button>
);

// ── Empty State ────────────────────────────────────────────────────────────────
const EmptyState = ({ query, onSuggestion }) => (
  <div className="text-center py-16">
    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Search size={22} className="text-gray-400" />
    </div>
    <p className="text-gray-700 font-medium mb-1">
      {query ? `No results for "${query}"` : 'Start searching'}
    </p>
    <p className="text-sm text-gray-400 mb-6">Try a different keyword or browse a suggestion below</p>
    <div className="flex flex-wrap gap-2 justify-center">
      {SUGGESTIONS.map((s) => (
        <button
          key={s}
          onClick={() => onSuggestion(s)}
          className="text-sm px-4 py-1.5 bg-indigo-50 text-indigo-600 rounded-full hover:bg-indigo-100 transition-colors font-medium"
        >
          #{s}
        </button>
      ))}
    </div>
  </div>
);

// ── Main SearchPage ────────────────────────────────────────────────────────────
const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const initialQuery = searchParams.get('q') || '';
  const [inputValue, setInputValue] = useState(initialQuery);
  const [activeTab, setActiveTab] = useState('All');
  const [results, setResults] = useState({ cases: [], opportunities: [], panels: [] });
  const [isLoading, setIsLoading] = useState(false);

  // Vets don't browse cases as students do — hide Cases tab for vets
  const visibleTabs = user?.role === 'veterinarian'
    ? TABS.filter((t) => t !== 'Cases')
    : TABS;

  const doSearch = async (q) => {
    if (!q.trim() || q.trim().length < 2) {
      setResults({ cases: [], opportunities: [], panels: [] });
      return;
    }
    setIsLoading(true);
    const [casesRes, oppsRes, panelsRes] = await Promise.allSettled([
      user?.role !== 'veterinarian' ? getCases({ q, limit: 10 }) : Promise.resolve({ data: [] }),
      getOpportunities({ q, limit: 10 }),
      searchPanels(q),
    ]);

    setResults({
      cases: casesRes.status === 'fulfilled' ? (casesRes.value?.data || casesRes.value || []) : [],
      opportunities: oppsRes.status === 'fulfilled' ? (oppsRes.value?.data?.data || oppsRes.value?.data || oppsRes.value || []) : [],
      panels: panelsRes.status === 'fulfilled' ? (panelsRes.value?.data || panelsRes.value || []) : [],
    });
    setIsLoading(false);
  };

  // Fire search when URL q param changes
  useEffect(() => {
    const q = searchParams.get('q') || '';
    setInputValue(q);
    doSearch(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim() });
      setActiveTab('All');
    }
  };

  const handleSuggestion = (s) => {
    setInputValue(s);
    setSearchParams({ q: s });
    setActiveTab('All');
  };

  const currentQuery = searchParams.get('q') || '';
  const totalCount = results.cases.length + results.opportunities.length + results.panels.length;

  // What to show per tab
  const showCases = activeTab === 'All' || activeTab === 'Cases';
  const showOpps = activeTab === 'All' || activeTab === 'Opportunities';
  const showPanels = activeTab === 'All' || activeTab === 'Panels';

  const visibleCases = showCases ? results.cases : [];
  const visibleOpps = showOpps ? results.opportunities : [];
  const visiblePanels = showPanels ? results.panels : [];
  const visibleTotal = visibleCases.length + visibleOpps.length + visiblePanels.length;

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-6">

        {/* Search input */}
        <form onSubmit={handleSubmit} className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              id="search-page-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Search cases, specialties, or keywords..."
              autoFocus
              className="w-full pl-11 pr-12 py-3 text-sm border border-gray-200 rounded-xl bg-white shadow-sm
                         focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100
                         placeholder:text-gray-400 transition-all duration-200"
            />
            {inputValue && (
              <button
                type="button"
                onClick={() => { setInputValue(''); setSearchParams({}); setResults({ cases: [], opportunities: [], panels: [] }); }}
                className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Search
            </button>
          </div>
        </form>

        {/* Stats bar */}
        {currentQuery && !isLoading && (
          <p className="text-sm text-gray-500 mb-4">
            {totalCount > 0
              ? <><span className="font-semibold text-gray-800">{totalCount}</span> results for "<span className="font-medium text-indigo-600">{currentQuery}</span>"</>
              : <>No results for "<span className="font-medium text-gray-700">{currentQuery}</span>"</>
            }
          </p>
        )}

        {/* Tab bar */}
        {currentQuery && (
          <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
            {visibleTabs.map((tab) => {
              const count = tab === 'Cases' ? results.cases.length
                : tab === 'Opportunities' ? results.opportunities.length
                : tab === 'Panels' ? results.panels.length
                : totalCount;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3.5 py-1.5 text-xs font-medium rounded-md transition-all duration-150 ${
                    activeTab === tab
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                  {!isLoading && count > 0 && (
                    <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                      activeTab === tab ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Results */}
        <div className="space-y-3">
          {isLoading ? (
            [1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)
          ) : visibleTotal > 0 ? (
            <>
              {/* Cases */}
              {visibleCases.length > 0 && (
                <div className="space-y-2">
                  {activeTab === 'All' && (
                    <div className="flex items-center gap-1.5 mt-1 mb-2">
                      <Stethoscope size={12} className="text-indigo-400" />
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Clinical Cases</span>
                    </div>
                  )}
                  {visibleCases.map((c) => (
                    <CaseCard key={c.id} c={c} query={currentQuery} onClick={() => navigate(`/cases/${c.id}`)} />
                  ))}
                </div>
              )}

              {/* Opportunities */}
              {visibleOpps.length > 0 && (
                <div className="space-y-2">
                  {activeTab === 'All' && (
                    <div className={`flex items-center gap-1.5 mb-2 ${visibleCases.length > 0 ? 'mt-4' : 'mt-1'}`}>
                      <Briefcase size={12} className="text-emerald-400" />
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Opportunities</span>
                    </div>
                  )}
                  {visibleOpps.map((o) => (
                    <OpportunityCard key={o.id} o={o} query={currentQuery} onClick={() => navigate('/opportunities')} />
                  ))}
                </div>
              )}

              {/* Panels */}
              {visiblePanels.length > 0 && (
                <div className="space-y-2">
                  {activeTab === 'All' && (
                    <div className={`flex items-center gap-1.5 mb-2 ${(visibleCases.length > 0 || visibleOpps.length > 0) ? 'mt-4' : 'mt-1'}`}>
                      <Users size={12} className="text-violet-400" />
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Discussion Panels</span>
                    </div>
                  )}
                  {visiblePanels.map((p) => (
                    <PanelCard key={p.id} p={p} query={currentQuery} onClick={() => navigate(`/panels/${p.id}`)} />
                  ))}
                </div>
              )}
            </>
          ) : (
            <EmptyState query={currentQuery} onSuggestion={handleSuggestion} />
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default SearchPage;
